import { useEffect, useState } from "react";
import { Modal, ModalBody, ModalHeader, ModalContent } from "@heroui/react";
import {
  EditableSelectComponent,
  renderSection,
} from "components";
import { AlertInfo, Checkmark, Edit, Settings, Wrench } from "assets/icons";
import ButtonNew from "components/buttonNew/buttonNew";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import SearchComponent, {
  SearchMode,
} from "shared/components/searchBar/components/searchComponent";
import { ResourceType } from "shared/types/resource";
import { ContactTags } from "shared/types/contacts";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css"; // Base styling

// Type assertion for React 18 compatibility
const DatePicker = ReactDatePicker as any;

import {
  WindowDto,
  GlassPlacement,
  OpeningDirection,
  PaneDivision,
  SillType,
  StoneType,
  WindowMaterial,
  WindowType,
  BuildingParticleStatus,
  WindowLocation,
} from "../../../../shared/types/window";
import { ImagesContainer } from "shared/components/imagesContainer/imagesContainer";
import { FileLabel } from "shared/types/file";

interface ModalWindowProps {
  isOpen: boolean;
  onClose: () => void;
  building: any;
  onWindowAdded: () => void;
  window: any;
}

const ModalWindow = ({
  isOpen,
  onClose,
  building,
  onWindowAdded,
  window,
}: ModalWindowProps) => {
  const [windowData, setWindowData] = useState<WindowDto>({} as WindowDto);
  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { session } = useSession();

  useEffect(() => {
    setWindowData(window || ({} as WindowDto));
    setEditMode(!window);
    setIsLoading(false);
  }, [window]);

  // Function to handle changes in input/select fields
  const handleChange = (key: keyof WindowDto, value: any) => {
    setWindowData((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  // Define the shared properties for the roof modal
  const sharedWindowProps = {
    editMode: editMode,
    isLoading: isLoading,
    onEditClick: () => {
      /* ... */
    },
    onSaveClick: () => {
      console.log("Saved data:", windowData);
      onClose();
    },
    originalState: windowData,
    editedState: windowData,
    setStateFunction: setWindowData,
  };

  const createContactFieldComponent = (
    label: any,
    key: any,
    resourceFilter: any,
    metadataFilter: any,
  ) => {
    return () => (
      <div className={editMode ? "grow p-2" : ""}>
        <SearchComponent
          type="regular"
          label={label}
          resourceFilter={resourceFilter}
          metadataFilter={metadataFilter}
          onSelect={(resource) =>
            setWindowData((prev: any) => ({
              ...prev,
              [key]: {
                resourceId: resource.resourceId,
                name: resource.name,
                type: resource.type,
              },
            }))
          }
          initialValue={
            (windowData as any)?.[key] ? (windowData as any)?.[key].name : null
          }
          mode={SearchMode.SEARCH_AND_PRESENT}
          editMode={editMode}
        />
      </div>
    );
  };

  const windowFieldsConfig = [
    {
      label: "Status",
      key: "status",
      Component: () => (
        <EditableSelectComponent
          label="Status"
          editMode={editMode}
          value={windowData.status || ""}
          options={Object.values(BuildingParticleStatus).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("status", value)}
        />
      ),
    },
    {
      label: "Type",
      key: "type",
      Component: () => (
        <EditableSelectComponent
          label="Type"
          editMode={editMode}
          value={windowData.status || ""}
          options={Object.values(WindowType).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("type", value)}
        />
      ),
    },
    {
      label: "Raamlocatie",
      key: "location",
      Component: () => (
        <EditableSelectComponent
          label="Raamlocatie"
          editMode={editMode}
          value={windowData.location || ""}
          options={Object.values(WindowLocation).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("location", value)}
        />
      ),
    },
    {
      label: "Beschrijving (onderscheid tussen eventueel andere ramen)",
      key: "description",
    },
    {
      label: "Materiaal",
      key: "windowMaterial",
      Component: () => (
        <EditableSelectComponent
          label="Materiaal"
          editMode={editMode}
          value={windowData.material || ""}
          options={Object.values(WindowMaterial).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("material", value)}
        />
      ),
    },
    { label: "Kleur profiel", key: "profileColor" },
    {
      label: "Type raamtabletten",
      key: "sillType",
      Component: () => (
        <EditableSelectComponent
          label="Type raamtabletten"
          editMode={editMode}
          value={windowData.sillType || ""}
          options={Object.values(SillType).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("sillType", value)}
        />
      ),
    },
    {
      label: "Type steen",
      key: "stoneType",
      Component: () => (
        <EditableSelectComponent
          label="Type steen"
          editMode={editMode}
          value={windowData.stoneType || ""}
          options={Object.values(StoneType).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("stoneType", value)}
        />
      ),
    },
    {
      label: "Glas plaatsing",
      key: "glassPlacement",
      Component: () => (
        <EditableSelectComponent
          label="Glas plaatsing"
          editMode={editMode}
          value={windowData.glassPlacement || ""}
          options={Object.values(GlassPlacement).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("glassPlacement", value)}
        />
      ),
    },
    { label: "Jaartal plaatsing", key: "yearPlacement" },
  ];

  const windowExtraFieldsConfig = [
    { label: "Ug waarde", key: "ugValue" },
    { label: "Dikte glass", key: "glassThickness" },
    {
      label: "Draairichting",
      key: "openingDirection",
      Component: () => (
        <EditableSelectComponent
          label="Draairichting"
          editMode={editMode}
          value={windowData.openingDirection || ""}
          options={Object.values(OpeningDirection).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("openingDirection", value)}
        />
      ),
    },
    {
      label: "Vakverdeling",
      key: "paneDivision",
      Component: () => (
        <EditableSelectComponent
          label="Vakverdeling"
          editMode={editMode}
          value={windowData.paneDivision || ""}
          options={Object.values(PaneDivision).map((value) => ({
            label: value,
            value,
          }))}
          onChange={(value) => handleChange("paneDivision", value)}
        />
      ),
    },
  ];

  const windowInstallerFieldsConfig = [
    {
      label: "Installateur",
      key: "installer",
      Component: createContactFieldComponent(
        "Zoek de installateur in de lijst van installateurs",
        "installer",
        [ResourceType.COMPANY, ResourceType.CONTACT],
        ContactTags.AannemerRuwbouw,
      ),
    },
    {
      label: "Opleveringsdatum",
      key: "deliveryDate",
      Component: () => (
        <div className="z-10 flex w-full flex-col gap-1.5">
          <p className="text-xs">Opleveringsdatum</p>
          <DatePicker
            onChange={(date: any) =>
              setWindowData((prev: any) => ({
                ...prev,
                deliveryDate: date,
              }))
            }
            selected={
              windowData.deliveryDate ? new Date(windowData.deliveryDate) : null
            }
            dateFormat="yyyy/MM/dd"
            className="w-full rounded-sm bg-white p-2.5 text-neutral-primary shadow-form-element placeholder:text-neutral-secondary-mid focus:ring-3 focus:ring-opacity-50"
          />
        </div>
      ),
    },
    {
      label: "Volgende inspectiedatum",
      key: "nextInspectionDate",
      Component: () => (
        <div className="z-10 flex w-full flex-col gap-1.5">
          <p className="text-xs">Opleveringsdatum</p>
          <DatePicker
            onChange={(date: any) =>
              setWindowData((prev: any) => ({
                ...prev,
                nextInspectionDate: date,
              }))
            }
            selected={
              windowData.nextInspectionDate
                ? new Date(windowData.nextInspectionDate)
                : null
            }
            dateFormat="yyyy/MM/dd"
            className="w-full rounded-sm bg-white p-2.5 text-neutral-primary shadow-form-element placeholder:text-neutral-secondary-mid focus:ring-3 focus:ring-opacity-50"
          />
        </div>
      ),
    },
  ];

  const handleSaveChanges = async () => {
    if (!windowData.location || !windowData.type) {
      return toast.error("Raamlocatie & Type zijn verplichte velden");
    }

    (windowData as any).building = { id: building.id };
    try {
      const response = await fetchWithAuth(
        `${API_URL}/building/create-window`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(windowData),
        },
        session,
      );

      if (response.ok) {
        toast("Window saved successfully!");
        setWindowData({} as WindowDto);
        onWindowAdded();
        setEditMode(false);
        onClose();
      } else {
        toast("Error saving window. Please try again.");
      }
    } catch (error) {
      console.error("Failed to save window: ", error);
      toast("Error saving window!");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setWindowData({} as WindowDto);
        onClose();
      }}
      size="5xl"
      shouldBlockScroll={true}
      scrollBehavior="inside"
      backdrop="blur"
      className="p-8"
    >
      <ModalContent className="flex flex-col gap-4 font-normal text-neutral-primary">
        <ModalHeader className="flex flex-row items-center justify-between p-0 font-normal">
          {windowData && building ? (
            <p>
              {building?.id} - {windowData?.type} ({windowData?.type}) bewerken{" "}
            </p>
          ) : (
            <p>Nieuw raam toevoegen</p>
          )}
          <div className="flex gap-6">
            <ButtonNew
              variant="primaryMoss"
              onClick={() => {
                if (!editMode) setEditMode(true);
                else {
                  handleSaveChanges();
                }
              }}
              icon={editMode ? <Checkmark /> : <Edit />}
              label={editMode ? "Bewaren" : "Bewerken"}
            />
          </div>
        </ModalHeader>
        <ModalBody className="grid gap-5 gap-y-8 px-0 sm:grid-cols-2">
          <div className="flex flex-col gap-2.5">
            {renderSection({
              title: "Algemeen",
              icon: <AlertInfo />,
              fieldsConfig: windowFieldsConfig,
              showEditToggle: false,
              ...sharedWindowProps,
            })}
          </div>
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-2.5">
              {renderSection({
                title: "Extra gegevens",
                icon: <Settings />,
                fieldsConfig: windowExtraFieldsConfig,
                showEditToggle: false,
                ...sharedWindowProps,
              })}
            </div>
            <div className="flex flex-col gap-2.5">
              {renderSection({
                title: "Installatie",
                icon: <Wrench />,
                fieldsConfig: windowInstallerFieldsConfig,
                showEditToggle: false,
                ...sharedWindowProps,
              })}
            </div>
          </div>
          <div className="col-span-full">
            <ImagesContainer
              buildingId={building.id}
              fileLabel={windowData.location == WindowLocation.FRONT ? FileLabel.WindowsFrontPicture : windowData.location == WindowLocation.BACK ? FileLabel.WindowsBackPicture : windowData.location == WindowLocation.SIDE_LEFT ? FileLabel.WindowsLeftPicture : FileLabel.WindowsRightPicture}
              title={`Foto's van ramen ${windowData.location} `}
            />
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ModalWindow;
